import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { ensureGuestId, getAnon } from "../utils/localStorage";
import { API_BASE_URL } from "../utils/constants";
import {
  AVATAR_CATALOG,
  AVATAR_UNLOCK_RULES,
  BADGE_CATALOG,
  FREE_AVATAR_IDS,
  getAvatarById,
  getBadgeById,
  getFallbackAvatarId,
} from "../data/avatarCatalog";

const STORAGE_KEY = "pixyquiz_avatar_profiles_v1";

const AvatarContext = createContext(null);

function normalizeBackendAvatarUrl(value) {
  const rawValue = String(value || "").trim();
  if (!rawValue) {
    return null;
  }

  if (
    rawValue.startsWith("http://") ||
    rawValue.startsWith("https://") ||
    rawValue.startsWith("data:") ||
    rawValue.startsWith("blob:")
  ) {
    return rawValue;
  }

  if (rawValue.startsWith("/media/")) {
    return `${API_BASE_URL}${rawValue}`;
  }

  if (rawValue.startsWith("media/")) {
    return `${API_BASE_URL}/${rawValue}`;
  }

  return null;
}

function buildBackendAvatar(url) {
  return {
    id: url,
    image: url,
    src: url,
    name: "Avatar profil",
    type: "backend",
    isBackendAvatar: true,
  };
}

function getUserBackendAvatar(user) {
  if (!user || user.avatar_status === "rejected") {
    return null;
  }

  return normalizeBackendAvatarUrl(
    user.avatar || user.avatar_url || user.profile_avatar || null
  );
}

function readStoredProfiles() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch (error) {
    return {};
  }
}

function createDefaultProfile(profileKey, pseudo) {
  return {
    key: profileKey,
    pseudo: pseudo || "Joueur",
    activeAvatarId: FREE_AVATAR_IDS[0],
    unlockedAvatarIds: [...FREE_AVATAR_IDS],
    unlockedBadgeIds: [],
    stats: {
      correctAnswers: 0,
      firstPlaceFinishes: 0,
    },
    updatedAt: new Date().toISOString(),
  };
}

function normalizeProfile(profileKey, pseudo, profile = null) {
  const baseProfile = profile || createDefaultProfile(profileKey, pseudo);
  const unlockedAvatarIds = Array.from(
    new Set([...(baseProfile.unlockedAvatarIds || []), ...FREE_AVATAR_IDS])
  ).filter(Boolean);

  const activeAvatarId = unlockedAvatarIds.includes(baseProfile.activeAvatarId)
    ? baseProfile.activeAvatarId
    : unlockedAvatarIds[0];

  return {
    ...baseProfile,
    key: profileKey,
    pseudo: pseudo || baseProfile.pseudo || "Joueur",
    activeAvatarId,
    unlockedAvatarIds,
    unlockedBadgeIds: Array.from(new Set(baseProfile.unlockedBadgeIds || [])),
    stats: {
      correctAnswers: Number(baseProfile.stats?.correctAnswers || 0),
      firstPlaceFinishes: Number(baseProfile.stats?.firstPlaceFinishes || 0),
    },
    updatedAt: new Date().toISOString(),
  };
}

function applyUnlockRules(profile) {
  const nextUnlockedAvatarIds = new Set(profile.unlockedAvatarIds || []);
  const nextUnlockedBadgeIds = new Set(profile.unlockedBadgeIds || []);

  AVATAR_UNLOCK_RULES.forEach((rule) => {
    if (rule.isUnlocked(profile.stats || {})) {
      nextUnlockedAvatarIds.add(rule.avatarId);
      if (rule.badgeId) {
        nextUnlockedBadgeIds.add(rule.badgeId);
      }
    }
  });

  const activeAvatarId = nextUnlockedAvatarIds.has(profile.activeAvatarId)
    ? profile.activeAvatarId
    : [...nextUnlockedAvatarIds][0];

  return {
    ...profile,
    activeAvatarId,
    unlockedAvatarIds: [...nextUnlockedAvatarIds],
    unlockedBadgeIds: [...nextUnlockedBadgeIds],
    updatedAt: new Date().toISOString(),
  };
}

function shouldMergeIntoAuthenticatedProfile(previousKey, nextKey) {
  return (
    previousKey &&
    nextKey &&
    previousKey !== nextKey &&
    nextKey.startsWith("user:") &&
    (previousKey.startsWith("guest:") || previousKey.startsWith("anon:"))
  );
}

function mergeProfiles(sourceProfile, targetProfile) {
  if (!sourceProfile) {
    return targetProfile;
  }

  const mergedUnlockedAvatarIds = Array.from(
    new Set([
      ...(targetProfile.unlockedAvatarIds || []),
      ...(sourceProfile.unlockedAvatarIds || []),
    ])
  );
  const mergedUnlockedBadgeIds = Array.from(
    new Set([
      ...(targetProfile.unlockedBadgeIds || []),
      ...(sourceProfile.unlockedBadgeIds || []),
    ])
  );
  const preferredActiveAvatarId = mergedUnlockedAvatarIds.includes(
    sourceProfile.activeAvatarId
  )
    ? sourceProfile.activeAvatarId
    : targetProfile.activeAvatarId;

  return {
    ...targetProfile,
    activeAvatarId: preferredActiveAvatarId,
    unlockedAvatarIds: mergedUnlockedAvatarIds,
    unlockedBadgeIds: mergedUnlockedBadgeIds,
    stats: {
      correctAnswers:
        Number(targetProfile.stats?.correctAnswers || 0) +
        Number(sourceProfile.stats?.correctAnswers || 0),
      firstPlaceFinishes:
        Number(targetProfile.stats?.firstPlaceFinishes || 0) +
        Number(sourceProfile.stats?.firstPlaceFinishes || 0),
    },
  };
}

function getCurrentIdentity(user) {
  const anon = getAnon();
  const localGuestId = ensureGuestId();

  if (user?.user_id || user?.id) {
    return {
      key: `user:${user.user_id || user.id}`,
      uid: String(user.user_id || user.id),
      pseudo: user.username || user.pseudo || "Joueur",
    };
  }

  if (anon?.uuid || anon?.id) {
    return {
      key: `anon:${anon.uuid || anon.id}`,
      uid: String(anon.uuid || anon.id),
      pseudo: anon.pseudo || "Invite",
    };
  }

  if (localGuestId) {
    return {
      key: `guest:${localGuestId}`,
      uid: String(localGuestId),
      pseudo: "Invite",
    };
  }

  return {
    key: "guest:local",
    uid: "guest:local",
    pseudo: "Invite",
  };
}

export function AvatarProvider({ children }) {
  const { user } = useAuth();
  const [profilesByKey, setProfilesByKey] = useState(() => readStoredProfiles());
  const previousIdentityRef = useRef(null);

  const identity = useMemo(() => getCurrentIdentity(user), [user]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profilesByKey));
  }, [profilesByKey]);

  useEffect(() => {
    setProfilesByKey((previousProfiles) => {
      const previousIdentity = previousIdentityRef.current;
      const nextProfiles = { ...previousProfiles };

      if (
        shouldMergeIntoAuthenticatedProfile(previousIdentity?.key, identity.key)
      ) {
        const sourceProfile = normalizeProfile(
          previousIdentity.key,
          previousIdentity.pseudo,
          previousProfiles[previousIdentity.key]
        );
        const targetProfile = normalizeProfile(
          identity.key,
          identity.pseudo,
          previousProfiles[identity.key]
        );
        nextProfiles[identity.key] = applyUnlockRules(
          mergeProfiles(sourceProfile, targetProfile)
        );
        delete nextProfiles[previousIdentity.key];
        previousIdentityRef.current = identity;
        return nextProfiles;
      }

      const currentProfile = normalizeProfile(
        identity.key,
        identity.pseudo,
        previousProfiles[identity.key]
      );

      previousIdentityRef.current = identity;
      return {
        ...previousProfiles,
        [identity.key]: applyUnlockRules(currentProfile),
      };
    });
  }, [identity]);

  const updateCurrentProfile = useCallback(
    (updater) => {
      setProfilesByKey((previousProfiles) => {
        const currentProfile = normalizeProfile(
          identity.key,
          identity.pseudo,
          previousProfiles[identity.key]
        );
        const updatedProfile = applyUnlockRules(updater(currentProfile));

        return {
          ...previousProfiles,
          [identity.key]: updatedProfile,
        };
      });
    },
    [identity.key, identity.pseudo]
  );

  const currentProfile = normalizeProfile(
    identity.key,
    identity.pseudo,
    profilesByKey[identity.key]
  );

  const backendAvatarUrl = getUserBackendAvatar(user);
  const currentAvatar = backendAvatarUrl
    ? buildBackendAvatar(backendAvatarUrl)
    : getAvatarById(currentProfile.activeAvatarId);

  const setActiveAvatar = useCallback(
    (avatarId) => {
      updateCurrentProfile((profile) => {
        if (!profile.unlockedAvatarIds.includes(avatarId)) {
          return profile;
        }

        return {
          ...profile,
          activeAvatarId: avatarId,
        };
      });
    },
    [updateCurrentProfile]
  );

  const trackCorrectAnswer = useCallback(() => {
    updateCurrentProfile((profile) => ({
      ...profile,
      stats: {
        ...profile.stats,
        correctAnswers: (profile.stats.correctAnswers || 0) + 1,
      },
    }));
  }, [updateCurrentProfile]);

  const trackFirstPlaceFinish = useCallback(() => {
    updateCurrentProfile((profile) => ({
      ...profile,
      stats: {
        ...profile.stats,
        firstPlaceFinishes: (profile.stats.firstPlaceFinishes || 0) + 1,
      },
    }));
  }, [updateCurrentProfile]);

  const syncBackendBadgeUnlocks = useCallback((badges = []) => {
    updateCurrentProfile((profile) => {
      const unlockedBadgeIds = new Set(profile.unlockedBadgeIds || []);
      const unlockedAvatarIds = new Set(profile.unlockedAvatarIds || []);

      badges.forEach((badge) => {
        const linkedAvatarId = badge?.avatar_id || badge?.avatarId || null;

        if (linkedAvatarId && getBadgeById(linkedAvatarId)) {
          unlockedBadgeIds.add(linkedAvatarId);
        }

        if (linkedAvatarId && getAvatarById(linkedAvatarId)) {
          unlockedAvatarIds.add(linkedAvatarId);
        }
      });

      return {
        ...profile,
        unlockedBadgeIds: [...unlockedBadgeIds],
        unlockedAvatarIds: [...unlockedAvatarIds],
      };
    });
  }, [updateCurrentProfile]);

  const getPlayerAvatar = useCallback(
    (player) => {
      const explicitAvatarId =
        player?.avatar ||
        player?.avatar_url ||
        player?.avatarUrl ||
        player?.profile_avatar ||
        player?.active_avatar ||
        player?.activeAvatar ||
        player?.avatar_id ||
        player?.avatarId ||
        null;

      const backendAvatar = normalizeBackendAvatarUrl(explicitAvatarId);
      if (backendAvatar) {
        return buildBackendAvatar(backendAvatar);
      }

      if (explicitAvatarId && getAvatarById(explicitAvatarId)) {
        return getAvatarById(explicitAvatarId);
      }

      const incomingUid = String(
        player?.uid || player?.player_id || player?.user_id || ""
      );
      const isCurrentPlayer =
        incomingUid && incomingUid === identity.uid
          ? true
          : player?.pseudo && player.pseudo === identity.pseudo;

      if (isCurrentPlayer && currentAvatar) {
        return currentAvatar;
      }

      const fallbackId = getFallbackAvatarId(
        incomingUid || player?.pseudo || player?.username || "pixy"
      );

      return getAvatarById(fallbackId) || currentAvatar || getAvatarById(FREE_AVATAR_IDS[0]);
    },
    [currentAvatar, identity.pseudo, identity.uid]
  );

  const unlockedAvatars = useMemo(
    () =>
      AVATAR_CATALOG.filter((avatar) =>
        currentProfile.unlockedAvatarIds.includes(avatar.id)
      ),
    [currentProfile.unlockedAvatarIds]
  );

  const lockedAvatars = useMemo(
    () =>
      AVATAR_CATALOG.filter(
        (avatar) => !currentProfile.unlockedAvatarIds.includes(avatar.id)
      ),
    [currentProfile.unlockedAvatarIds]
  );

  const unlockedBadges = useMemo(
    () =>
      currentProfile.unlockedBadgeIds
        .map((badgeId) => getBadgeById(badgeId))
        .filter(Boolean),
    [currentProfile.unlockedBadgeIds]
  );

  const missionProgress = useMemo(
    () =>
      AVATAR_UNLOCK_RULES.map((rule) => ({
        ...rule,
        unlocked: currentProfile.unlockedAvatarIds.includes(rule.avatarId),
        progress: rule.progressLabel(currentProfile.stats || {}),
      })),
    [currentProfile.stats, currentProfile.unlockedAvatarIds]
  );

  const value = useMemo(
    () => ({
      avatarCatalog: AVATAR_CATALOG,
      badgeCatalog: BADGE_CATALOG,
      currentProfile,
      currentAvatar,
      currentPlayerKey: identity.key,
      currentPlayerUid: identity.uid,
      setActiveAvatar,
      trackCorrectAnswer,
      trackFirstPlaceFinish,
      syncBackendBadgeUnlocks,
      getPlayerAvatar,
      unlockedAvatars,
      lockedAvatars,
      unlockedBadges,
      missionProgress,
    }),
    [
      currentAvatar,
      currentProfile,
      getPlayerAvatar,
      identity.key,
      identity.uid,
      lockedAvatars,
      missionProgress,
      setActiveAvatar,
      syncBackendBadgeUnlocks,
      trackCorrectAnswer,
      trackFirstPlaceFinish,
      unlockedAvatars,
      unlockedBadges,
    ]
  );

  return (
    <AvatarContext.Provider value={value}>{children}</AvatarContext.Provider>
  );
}

export function useAvatar() {
  return useContext(AvatarContext);
}
